import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Users, Loader2, RefreshCw, Camera, CornerDownRight, Send, X as CloseIcon, Flame, Trophy, ThumbsUp, ChevronRight, MessageSquare, LayoutGrid, BarChart3 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../i18n';
import { favoriteService } from '../../services/favoriteService';
import { fetchAllRecentComments, likeComment, unlikeComment, deleteComment, reportComment, createComment } from '../../services/commentService';
import { fetchMatchDetails } from '../../services/footballService';
import { CommentWithUser } from '../../types';
import { CommunityCommentCard } from '../Community/CommunityCommentCard';
import { EmptyState } from '../Common/EmptyState';
import { XlevenLogo } from '../Common/XlevenLogo';
import SEO from '../Common/SEO';
import { ReportModal } from '../Common/ReportModal';
import { ConfirmModal } from '../Common/ConfirmModal';
import { uploadSharedImage } from '../../services/authService';
import { HotMatchSlider } from '../Community/HotMatchSlider';
import { PhantomStadium } from '../Community/PhantomStadium';
import { RouteState } from '../../types';

interface CommunityViewProps {
  route?: RouteState;
  isPostModalOpen?: boolean;
  setIsPostModalOpen?: (open: boolean) => void;
}

export const CommunityView: React.FC<CommunityViewProps> = ({ route, isPostModalOpen, setIsPostModalOpen }) => {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [comments, setComments] = useState<(CommentWithUser & { is_liked?: boolean, match_info?: any })[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [replyingTo, setReplyingTo] = useState<CommentWithUser | null>(null);
  const [showInput, setShowInput] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [attachedImageFile, setAttachedImageFile] = useState<File | null>(null);
  const [attachedImageUrl, setAttachedImageUrl] = useState<string | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [commentToReport, setCommentToReport] = useState<string | null>(null);
  const [isReporting, setIsReporting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasFavoriteTeams, setHasFavoriteTeams] = useState<boolean>(false);
  const [stadiumRefreshTrigger, setStadiumRefreshTrigger] = useState(0);

  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentPath = window.getCurrentPath?.() || '';
  const activeSubTab = currentPath.includes('/stadium') ? 'stadium' : 'talk';

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      if (user) {
        const favorites = await favoriteService.getFavoriteTeams();
        setHasFavoriteTeams(favorites && favorites.length > 0);
      } else {
        setHasFavoriteTeams(false);
      }

      const recentComments = await fetchAllRecentComments(40);

      const matchIds = Array.from(new Set(
        recentComments
          .map(c => c.post_id)
          .filter(id => id && id !== 'community' && !isNaN(Number(id)))
      ));

      const matchInfoMap = new Map();
      if (matchIds.length > 0) {
          await Promise.all(matchIds.map(async (id) => {
            try {
              const detail = await fetchMatchDetails(id, undefined, false, { 
                stats: false, events: false, lineups: false, players: false, injuries: false 
              });
              if (detail) matchInfoMap.set(id, detail);
            } catch (e) {
              console.error(`Failed to fetch match info for ${id}`, e);
            }
          }));
      }
      
      const finalComments = recentComments.map(c => {
        let matchInfo = null;
        if (c.home_team_id && c.away_team_id) {
          matchInfo = {
            id: c.post_id,
            homeTeam: {
              id: c.home_team_id,
              name: c.home_team_name || '',
              logo: c.home_team_logo || `https://media.api-sports.io/football/teams/${c.home_team_id}.png`
            },
            awayTeam: {
              id: c.away_team_id,
              name: c.away_team_name || '',
              logo: c.away_team_logo || `https://media.api-sports.io/football/teams/${c.away_team_id}.png`
            }
          };

          const liveData = matchInfoMap.get(c.post_id);
          if (liveData) {
             matchInfo = {
               ...matchInfo,
               status: liveData.status,
               time: liveData.time,
               score: liveData.score,
               homeTeam: { ...matchInfo.homeTeam, score: liveData.homeTeam.score },
               awayTeam: { ...matchInfo.awayTeam, score: liveData.awayTeam.score },
               league: liveData.league,
               leagueName: liveData.leagueName
             };
          }
        }
        return {
          ...c,
          match_info: matchInfo
        };
      });

      setComments(finalComments);
    } catch (error) {
      console.error("Failed to load community data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, activeSubTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLike = useCallback(async (commentId: string | number) => {
    if (!user) {
        window.navigateTo('/login');
        return;
    }

    setComments(prev => {
      const comment = prev.find(c => c.id === commentId);
      if (!comment) return prev;
      const wasLiked = comment.is_liked;
      return prev.map(c => 
        c.id === commentId 
          ? { ...c, is_liked: !wasLiked, likes_count: c.likes_count + (wasLiked ? -1 : 1) } 
          : c
      );
    });

    try {
      const targetComment = comments.find(c => c.id === commentId);
      if (targetComment?.is_liked) await unlikeComment(commentId.toString());
      else await likeComment(commentId.toString());
    } catch (error) {
      loadData(true);
    }
  }, [user, comments, loadData]);

  const handleReply = useCallback((comment: CommentWithUser) => {
    if (!user) {
        window.navigateTo('/login');
        return;
    }
    setReplyingTo(comment);
    setShowInput(true);
    setTimeout(() => commentInputRef.current?.focus(), 100);
  }, [user]);

  const handlePostComment = async () => {
    if (!user || (!newComment.trim() && !attachedImageUrl)) return;
    
    setIsPosting(true);
    try {
        let finalImageUrl = attachedImageUrl;
        if (attachedImageFile) {
            finalImageUrl = await uploadSharedImage(user.id, attachedImageFile);
        }

        await createComment({
            postId: replyingTo?.post_id || 'community',
            content: newComment,
            imageUrl: finalImageUrl,
            parentId: replyingTo?.id ? String(replyingTo.id) : null,
        });

        loadData(true);
        setNewComment('');
        if (commentInputRef.current) commentInputRef.current.style.height = 'auto';
        setAttachedImageFile(null);
        setAttachedImageUrl(null);
        setReplyingTo(null);
        setShowInput(false);
    } catch (error) {
        console.error("Failed to post comment:", error);
    } finally {
        setIsPosting(false);
    }
  };

  const handleImageFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    const localPreviewUrl = URL.createObjectURL(file);
    setAttachedImageFile(file);
    setAttachedImageUrl(localPreviewUrl);
  };

  const handleReport = useCallback((commentId: string | number) => {
    if (!user) {
        window.navigateTo('/login');
        return;
    }
    setCommentToReport(commentId.toString());
    setReportModalOpen(true);
  }, [user]);

  const executeReport = async (reason: string) => {
    if (!commentToReport) return;
    setIsReporting(true);
    try {
        await reportComment(commentToReport, reason);
        alert(t(lang, 'reportCompleted'));
        setReportModalOpen(false);
        setCommentToReport(null);
    } catch (e) {
        console.error(e);
    } finally {
        setIsReporting(false);
    }
  };

  const handleDelete = useCallback((commentId: string | number) => {
    setCommentToDelete(commentId);
    setDeleteModalOpen(true);
  }, []);

  const executeDelete = async () => {
    if (!commentToDelete) return;
    setIsDeleting(true);
    try {
        await deleteComment(commentToDelete.toString());
        setComments(prev => prev.filter(c => c.id !== commentToDelete));
        setDeleteModalOpen(false);
        setCommentToDelete(null);
    } catch (e) {
        console.error(e);
    } finally {
        setIsDeleting(false);
    }
  };

  const hotMatches = useMemo(() => {
    const hotMatchesMap = new Map();
    comments.forEach(c => {
      if (c.match_info && !hotMatchesMap.has(c.match_info.id)) {
          hotMatchesMap.set(c.match_info.id, c.match_info);
      }
    });
    return Array.from(hotMatchesMap.values()).slice(0, 10);
  }, [comments]);

  const bestComments = useMemo(() => {
    return comments
      .filter(c => c.likes_count >= 3)
      .sort((a, b) => b.likes_count - a.likes_count)
      .slice(0, 5);
  }, [comments]);

  return (
    <div className="min-h-screen bg-white">
      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onConfirm={executeReport}
        isLoading={isReporting}
        lang={lang}
      />
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
            setDeleteModalOpen(false);
            setCommentToDelete(null);
        }}
        onConfirm={executeDelete}
        title={t(lang, 'deleteCommentTitle')}
        confirmLabel={t(lang, 'delete')}
        cancelLabel={t(lang, 'cancel')}
        isLoading={isDeleting}
      />
      <SEO 
        title={`${t(lang, 'community')} | XLEVEN`}
        description={t(lang, 'realTimeMatchTalk')}
      />

      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="flex flex-col">
            <div className="px-4 h-14 flex items-center justify-between">
                <div className="md:hidden">
                    <XlevenLogo />
                </div>
                <div className="hidden md:block" />
                
                <button 
                    onClick={() => {
                        if (activeSubTab === 'talk') loadData(true);
                        else if (activeSubTab === 'stadium') setStadiumRefreshTrigger(prev => prev + 1);
                    }}
                    className={`p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all rounded-full ${refreshing ? 'animate-spin text-rose-500' : ''}`}
                    title="Refresh"
                >
                    <RefreshCw size={20} strokeWidth={2.5} />
                </button>
            </div>

            <div className="md:fixed md:pt-6 px-4">
                <nav className="flex items-center gap-6 overflow-x-auto no-scrollbar">
                    <button 
                        onClick={() => {
                            if (loading) return;
                            window.navigateTo('/community');
                        }}
                        disabled={loading}
                        className={`pb-3 text-[13px] font-black uppercase tracking-widest transition-all relative ${activeSubTab === 'talk' ? 'text-slate-900' : 'text-slate-400 hover:text-slate-600'} ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {t(lang, 'matchTalk')}
                        {activeSubTab === 'talk' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-900 rounded-full" />}
                    </button>
                    <button 
                        onClick={() => {
                            if (loading) return;
                            if (!user) {
                                window.navigateTo('/login');
                            } else if (!hasFavoriteTeams) {
                                alert(t(lang, 'noFavoriteTeams') || '관심 팀을 설정해주세요');
                            } else {
                                window.navigateTo('/community/stadium');
                            }
                        }}
                        disabled={loading}
                        className={`pb-3 text-[13px] font-black uppercase tracking-widest transition-all relative ${activeSubTab === 'stadium' ? 'text-slate-900' : 'text-slate-400 hover:text-slate-600'} ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {t(lang, 'phantomStadium')}
                        {activeSubTab === 'stadium' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-900 rounded-full" />}
                    </button>
                </nav>
            </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto border-x border-gray-50 min-h-screen pb-32">
        {loading && !refreshing ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <Loader2 className="animate-spin text-rose-500" size={32} />
          </div>
        ) : (
          <>
            {activeSubTab === 'talk' && (
              <>
                <HotMatchSlider hotMatches={hotMatches} lang={lang} />
                {bestComments.length > 0 && (
          <div className="bg-gradient-to-b from-rose-50/30 to-transparent pt-6 pb-2">
            <div className="px-4 mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-rose-500 flex items-center justify-center shadow-lg shadow-rose-500/20">
                  <Flame size={18} className="text-white fill-current" />
                </div>
                <div>
                  <h3 className="text-[15px] font-black text-slate-900 uppercase tracking-tight">Best Intelligence</h3>
                  <p className="text-[10px] font-bold text-rose-500/80 uppercase tracking-[0.1em]">{t(lang, 'hotTopics')}</p>
                </div>
              </div>
            </div>
            
            <div className="flex overflow-x-auto gap-3 px-4 no-scrollbar pb-4">
              {bestComments.map((comment) => (
                <div key={`best-${comment.id}`} className="min-w-[280px] max-w-[280px]">
                  <div className="bg-white border-2 border-rose-100 rounded-2xl p-4 shadow-sm relative overflow-hidden group hover:border-rose-300 transition-all">
                    <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                      <Trophy size={40} className="text-rose-500" />
                    </div>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 rounded-full overflow-hidden border border-gray-100">
                        <img 
                          src={comment.user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.user?.name || 'U')}&background=random`} 
                          alt="" 
                          className="w-full h-full object-cover" 
                        />
                      </div>
                      <span className="text-xs font-black text-slate-900 truncate flex-1">{comment.user?.name}</span>
                      <div className="flex items-center gap-1 text-rose-500 font-bold text-[10px]">
                         <ThumbsUp size={10} fill="currentColor" />
                         {comment.likes_count}
                      </div>
                    </div>

                    <p className="text-[13px] text-slate-700 font-medium line-clamp-2 leading-relaxed mb-3">
                      {comment.content}
                    </p>

                    <button 
                      onClick={() => {
                        const el = document.getElementById(`comment-${comment.id}`);
                        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        el?.classList.add('bg-rose-50');
                        setTimeout(() => el?.classList.remove('bg-rose-50'), 2000);
                      }}
                      className="text-[10px] font-black text-rose-500 uppercase tracking-widest hover:text-rose-600 flex items-center gap-1"
                    >
                      {t(lang, 'viewDetail')} <ChevronRight size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

                <div className="divide-y divide-gray-50">
                {comments.length > 0 ? (
                    comments.map((comment) => (
                        <CommunityCommentCard 
                        key={comment.id}
                        comment={comment}
                        onLike={handleLike}
                        onReply={handleReply}
                        onReport={handleReport}
                        onDelete={handleDelete}
                        currentUserId={user?.id}
                        />
                    ))
                ) : (
                    <div className="py-24">
                        <EmptyState 
                            icon={Users}
                            title={t(lang, 'noContentFound')}
                            description={t(lang, 'firstIntelligenceReport')}
                            lang={lang}
                        />
                    </div>
                )}
                </div>
              </>
            )}

            {activeSubTab === 'stadium' && (
              user && hasFavoriteTeams ? (
                <PhantomStadium 
                    route={route} 
                    refreshTrigger={stadiumRefreshTrigger}
                    onLoadingChange={setRefreshing}
                    isPostModalOpenExternal={isPostModalOpen}
                    onPostModalCloseExternal={() => setIsPostModalOpen?.(false)}
                />
            ) : (
                <div className="py-24 px-6 text-center">
                    <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                        <Users size={40} className="text-slate-200" />
                    </div>
                    <h3 className="text-xl font-black text-slate-900 mb-2 uppercase tracking-tight">
                        {user ? t(lang, 'noFavoriteTeams') || '관심 팀을 설정해주세요' : t(lang, 'loginRequired') || '로그인이 필요합니다'}
                    </h3>
                    <p className="text-slate-500 text-[13px] font-medium leading-relaxed mb-8 max-w-[280px] mx-auto">
                        {user 
                            ? t(lang, 'addFavoriteTeamToAccessStadium') || '팬텀 스타디움을 이용하시려면 최소 한 개 이상의 관심 팀을 설정해야 합니다.' 
                            : t(lang, 'loginToAccessStadium') || '팬텀 스타디움은 회원 전용 공간입니다. 로그인 후 이용해주세요.'}
                    </p>
                    <button 
                        onClick={() => window.navigateTo?.(user ? '/games' : '/login')}
                        className="bg-slate-900 text-white px-8 py-3.5 rounded-2xl text-[13px] font-black uppercase tracking-widest hover:bg-rose-500 transition-all active:scale-95 shadow-xl shadow-slate-200"
                    >
                        {user ? t(lang, 'goSelectTeams') || '팀 선택하러 가기' : t(lang, 'goToLogin') || '로그인 하러 가기'}
                    </button>
                </div>
              )
            )}
          </>
        )}
      </main>
      
      <div className={`fixed bottom-[65px] md:bottom-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-all duration-500 ease-[0.32,0.72,0,1] ${showInput ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
        <div className="w-full max-w-[1200px] flex">
          <div className="hidden md:block md:w-20 lg:w-64 shrink-0" />
          <div className="flex-1 flex justify-center min-w-0 pointer-events-none">
            <div className="w-full max-w-2xl px-4 pb-4 md:pb-6 pointer-events-auto">
              <div className="relative bg-white/90 backdrop-blur-xl border border-gray-100 rounded-[28px] shadow-[0_-10px_40px_rgba(0,0,0,0.08)] overflow-hidden transition-all duration-300 focus-within:ring-2 focus-within:ring-rose-500/20">
            {isPosting && (
              <div className="absolute inset-0 z-50 bg-white/80 backdrop-blur-md flex flex-col items-center justify-center gap-3">
                <Loader2 className="animate-spin text-rose-500" size={24} />
              </div>
            )}

            {(replyingTo || attachedImageUrl) && (
              <div className="bg-slate-50/50 border-b border-gray-50 flex flex-col gap-2">
                {replyingTo && (
                  <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100/50">
                    <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 truncate bg-white px-2 py-1 rounded-lg border border-gray-100">
                      <CornerDownRight size={10} className="text-indigo-500" />
                      <span>{t(lang, 'replyingTo')} <span className="text-slate-900 font-black">{replyingTo.user?.name}</span></span>
                    </div>
                    <button onClick={() => { setReplyingTo(null); if (!newComment.trim()) setShowInput(false); }} className="text-slate-300 hover:text-rose-500 transition-colors">
                      <CloseIcon size={14} strokeWidth={3} />
                    </button>
                  </div>
                )}
                {attachedImageUrl && (
                  <div className="p-3 flex justify-start">
                    <div className="relative shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white">
                      <img src={attachedImageUrl} className="w-full h-full object-cover" alt="" />
                      <button 
                        onClick={() => { setAttachedImageFile(null); setAttachedImageUrl(null); }} 
                        className="absolute top-1 right-1 w-6 h-6 bg-slate-900/80 text-white rounded-full flex items-center justify-center shadow-lg"
                      >
                        <CloseIcon size={12} strokeWidth={4} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-end gap-2 p-2 pl-3">
              <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageFileSelect} />
              
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-rose-500 transition-all shrink-0"
              >
                <Camera size={20} />
              </button>

              <textarea
                ref={commentInputRef}
                className="flex-1 bg-transparent border-none focus:ring-0 text-[14px] font-medium text-slate-800 placeholder:text-slate-300 resize-none py-2.5 h-10 max-h-32 custom-scrollbar focus:outline-none"
                placeholder={t(lang, 'joinConversation')}
                onBlur={() => {
                  if (!newComment.trim() && !replyingTo && !attachedImageUrl) {
                    setShowInput(false);
                  }
                }}
                value={newComment}
                rows={1}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = `${target.scrollHeight}px`;
                }}
                onChange={(e) => setNewComment(e.target.value)}
                disabled={isPosting}
              />

                  <button
                    onClick={handlePostComment}
                    disabled={(!newComment.trim() && !attachedImageUrl) || isPosting}
                    className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-rose-600 transition-all active:scale-95 disabled:opacity-20 shrink-0"
                  >
                    <Send size={18} className="ml-0.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="hidden md:block w-80 shrink-0" />
        </div>
      </div>
    </div>
  );
};
